import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/Button/Button';
import { Chip } from '@/components/Chip/Chip';
import { Alert } from '@/components/Alert/Alert';
import { FilterChip } from '@/components/FilterChip/FilterChip';
import { createColumnHelper, DataTable, type ColumnDef } from '@/components/DataTable/DataTable';
import { t } from '@/lib/i18n';
import { formatRelativeTime } from '@/lib/format';
import { useCustomizeLibraryItem, useDeleteLibraryItem, useLibraryList } from '../hooks';
import type { LibraryItem, LibraryKind, SystemItem, TenantItem } from '../schemas';
import { DeleteConfirmDialog } from './DeleteConfirmDialog';
import styles from './LibraryList.module.css';
import { formatCustomizeError } from './SystemReadOnlyView';

interface LibraryListProps {
  kind: LibraryKind;
}

type SourceFilter = 'all' | 'tenant' | 'system';

const columnHelper = createColumnHelper<LibraryItem>();

export function LibraryList({ kind }: LibraryListProps) {
  const navigate = useNavigate();
  const list = useLibraryList(kind);
  const customize = useCustomizeLibraryItem(kind);
  const del = useDeleteLibraryItem(kind);
  const [pendingDelete, setPendingDelete] = useState<TenantItem | null>(null);
  const [customizingId, setCustomizingId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [customizeError, setCustomizeError] = useState<string | null>(null);
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');

  const items: LibraryItem[] = useMemo(() => list.data?.items ?? [], [list.data]);
  const labels = labelsFor(kind);

  const tenantCount = items.filter((i) => i.kind === 'tenant').length;
  const systemCount = items.filter((i) => i.kind === 'system').length;

  const filteredItems = useMemo(() => {
    if (sourceFilter === 'all') return items;
    return items.filter((i) => i.kind === sourceFilter);
  }, [items, sourceFilter]);

  function goToEdit(item: TenantItem) {
    setCustomizeError(null);
    void navigate(`/library/${routeSegment(kind)}/${item.id}`);
  }

  function startCustomize(item: SystemItem) {
    setCustomizingId(item.id);
    setCustomizeError(null);
    customize.mutate(
      { systemRowId: item.id },
      {
        onSuccess: ({ item: newItem, migratedMemberCount }) => {
          void navigate(`/library/${routeSegment(kind)}/${newItem.id}`, {
            state: { customizedFrom: item.name, migratedMemberCount },
          });
        },
        onError: (err) => setCustomizeError(formatCustomizeError(err)),
        onSettled: () => setCustomizingId(null),
      },
    );
  }

  function onConfirmDelete() {
    if (!pendingDelete) return;
    setDeleteError(null);
    del.mutate(
      { id: pendingDelete.id },
      {
        onSuccess: () => setPendingDelete(null),
        onError: (err) => setDeleteError(formatDeleteError(err)),
      },
    );
  }

  const columns: ColumnDef<LibraryItem, unknown>[] = [
    columnHelper.accessor((item) => item.name, {
      id: 'name',
      header: t('Name'),
      cell: (info) => {
        const item = info.row.original;
        return (
          <Link to={`/library/${routeSegment(kind)}/${item.id}`} className={styles.nameCell}>
            <span className={styles.name}>{item.name}</span>
            {item.description ? <span className={styles.desc}>{item.description}</span> : null}
          </Link>
        );
      },
    }),
    columnHelper.accessor((item) => item.kind, {
      id: 'source',
      header: t('Source'),
      cell: (info) => (
        <Chip variant="neutral" size="sm">
          {info.getValue() === 'tenant' ? t('Yours') : t('Linqage')}
        </Chip>
      ),
      sortingFn: 'basic',
    }),
    columnHelper.accessor((item) => item.memberCount, {
      id: 'memberCount',
      header: t('Used by'),
      cell: (info) => t('{n} {noun}', { n: String(info.getValue()), noun: labels.memberNoun }),
    }),
    columnHelper.accessor((item) => item.updatedAt, {
      id: 'updatedAt',
      header: t('Updated'),
      cell: (info) => formatRelativeTime(info.getValue()),
    }),
    columnHelper.display({
      id: 'actions',
      header: '',
      cell: (info) => {
        const item = info.row.original;
        if (item.kind === 'tenant') {
          return (
            <div className={styles.rowActions}>
              <Button variant="ghost" size="sm" onClick={() => goToEdit(item)}>
                {t('Edit')}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setCustomizeError(null);
                  setPendingDelete(item);
                }}
              >
                {t('Delete')}
              </Button>
            </div>
          );
        }
        return (
          <div className={styles.rowActions}>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => startCustomize(item)}
              isLoading={customize.isPending && customizingId === item.id}
            >
              {t('Customize')}
            </Button>
          </div>
        );
      },
    }),
  ];

  return (
    <div className={styles.wrap}>
      {customizeError ? (
        <Alert variant="error" onDismiss={() => setCustomizeError(null)}>
          {customizeError}
        </Alert>
      ) : null}

      <DataTable
        data={filteredItems}
        columns={columns}
        ariaLabel={labels.tableLabel}
        defaultSort={{ id: 'name' }}
        isLoading={list.isLoading}
        emptyState={sourceFilter === 'all' ? labels.empty : t('No matches for this filter.')}
        searchPlaceholder={labels.searchPlaceholder}
        searchAccessor={(row) => `${row.name} ${row.description ?? ''}`}
        filterChips={
          <>
            <FilterChip
              label={t('All')}
              count={items.length}
              isActive={sourceFilter === 'all'}
              onClick={() => setSourceFilter('all')}
            />
            <FilterChip
              label={t('Yours')}
              count={tenantCount}
              isActive={sourceFilter === 'tenant'}
              onClick={() => setSourceFilter('tenant')}
            />
            <FilterChip
              label={t('Linqage')}
              count={systemCount}
              isActive={sourceFilter === 'system'}
              onClick={() => setSourceFilter('system')}
            />
          </>
        }
        toolbarActions={
          <Button
            variant="primary"
            size="md"
            onClick={() => {
              setCustomizeError(null);
              void navigate(`/library/${routeSegment(kind)}/new`);
            }}
          >
            {labels.newButton}
          </Button>
        }
      />

      {pendingDelete ? (
        <DeleteConfirmDialog
          itemLabel={pendingDelete.name}
          memberCount={pendingDelete.memberCount}
          memberNoun={labels.memberNoun}
          isPending={del.isPending}
          error={deleteError}
          onCancel={() => setPendingDelete(null)}
          onConfirm={onConfirmDelete}
        />
      ) : null}
    </div>
  );
}

function routeSegment(kind: LibraryKind): string {
  return kind === 'position' ? 'positions' : 'departments';
}

function formatDeleteError(err: unknown): string {
  if (err instanceof Error) {
    if ('status' in err) {
      const status = (err as { status: number }).status;
      if (status === 404) return 'This item is no longer available.';
      if (status === 403) return "You don't have permission to delete from the Library.";
    }
    if (err.message.toLowerCase().includes('network')) {
      return "Couldn't reach the server. Check your connection and try again.";
    }
  }
  return 'Delete failed. Try again.';
}

function labelsFor(kind: LibraryKind): {
  newButton: string;
  empty: string;
  memberNoun: string;
  searchPlaceholder: string;
  tableLabel: string;
} {
  if (kind === 'position') {
    return {
      newButton: t('+ New Position'),
      empty: t('No Positions in your Library yet.'),
      memberNoun: t('Members'),
      searchPlaceholder: t('Search Positions…'),
      tableLabel: t('Positions'),
    };
  }
  return {
    newButton: t('+ New Department'),
    empty: t('No Departments in your Library yet.'),
    memberNoun: t('Members'),
    searchPlaceholder: t('Search Departments…'),
    tableLabel: t('Departments'),
  };
}
